<script setup>
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import XlAvatar from '../xl/XlAvatar.vue';
import IconMail from '../icons/IconMail.vue';

/**
 * Home's Your advisor (XN-4734): the broker, a way to message them, and how
 * many messages are waiting. No broker names the agency instead (XN-4656).
 * `replyTime`, `available` and `phone` are slots for data that does not exist
 * yet: each renders nothing until it is given.
 */
const props = defineProps({
  // undefined: not known yet (hidden); null: no broker; object: the broker.
  broker: { type: Object, default: undefined },
  unreadMessages: { type: Number, default: null },
  agency: { type: String, default: '' },
  replyTime: { type: String, default: '' },
  available: { type: Boolean, default: false },
  phone: { type: String, default: '' },
});

const who = computed(() => (props.broker
  ? { name: props.broker.name || 'Your broker', role: 'Your insurance advisor', photo: props.broker.photo || '' }
  : { name: props.agency, role: 'Your contact', photo: '' }));
const unread = computed(() => (props.unreadMessages > 0
  ? `${props.unreadMessages} new message${props.unreadMessages === 1 ? '' : 's'}`
  : ''));
const tel = computed(() => {
  const digits = props.phone.replace(/\D/g, '');
  return digits.length >= 7 ? `tel:${props.phone.trim().startsWith('+') ? '+' : ''}${digits}` : '';
});
const callLabel = computed(() => `${props.broker?.first_name || 'Your broker'} is free now — call`);
</script>

<template>
  <section v-if="broker !== undefined" class="adv" data-testid="home-adviser">
    <div class="adv__sh"><h2>Your advisor</h2><span class="adv__ln" /></div>
    <div class="adv__card">
      <XlAvatar :name="who.name" :src="who.photo" :size="64" />
      <div class="adv__who">
        <b class="adv__name">{{ who.name }}</b>
        <span class="adv__role">{{ who.role }}<template v-if="replyTime"> · <span data-testid="adviser-reply">{{ replyTime }}</span></template></span>
        <RouterLink v-if="unread" to="/hub/messages" class="adv__unread" data-testid="adviser-unread">{{ unread }}</RouterLink>
      </div>
      <div class="adv__actions">
        <RouterLink to="/hub/messages" class="adv__msg"><IconMail color="currentColor" size="17" />Message</RouterLink>
        <a v-if="broker && available && tel" :href="tel" class="adv__call" data-testid="adviser-call">{{ callLabel }}</a>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* Section header as Coming up's: prototype `.sh`, 34px above. */
.adv__sh { display: flex; align-items: center; gap: 12px; margin: 34px 0 14px; }
.adv__sh h2 { margin: 0; font-size: 13px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--xl-color-outline-variant); }
.adv__ln { flex: 1; height: 1px; background: var(--xl-color-border); }
.adv__card {
  display: flex; align-items: center; gap: 20px; padding: 22px 24px;
  background: #fff; border: 1px solid var(--xl-color-border); border-radius: 24px; box-shadow: var(--xl-shadow-1);
}
.adv__who { flex: 1; min-width: 0; display: grid; gap: 4px; justify-items: start; }
.adv__name { font-size: 20px; font-weight: 600; letter-spacing: -0.02em; color: var(--xl-color-primary-text); }
.adv__role { font-size: 14px; color: var(--xl-color-secondary-text); }
.adv__unread {
  margin-top: 4px; padding: 4px 12px; border-radius: 999px; font-size: 13px; font-weight: 600;
  background: var(--xl-color-primary-container); color: var(--xl-color-primary); text-decoration: none;
}
.adv__actions { display: flex; gap: 10px; flex-wrap: wrap; }
/* Links, so XlButton's filled and outlined looks rather than XlButton itself. */
.adv__msg, .adv__call {
  display: inline-flex; align-items: center; gap: 8px; height: 42px; padding: 0 20px; border-radius: 999px;
  font-size: 14px; font-weight: 600; text-decoration: none;
}
.adv__msg {
  background: linear-gradient(155deg, var(--xl-color-surface-tint), var(--xl-color-primary));
  box-shadow: 0 5px 16px rgba(94, 47, 120, .26); color: var(--xl-color-on-primary);
}
.adv__call { background: #fff; border: 1px solid var(--xl-color-border); color: var(--xl-color-primary-text); }
@media (max-width: 700px) {
  .adv__card { flex-wrap: wrap; }
  .adv__actions { width: 100%; }
}
</style>
